import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { AuthGuard } from "./guards/AuthGuard";
import { AppLayout } from "./components/layout/AppLayout";
import { GradientSpinner } from "./components/common/GradientSpinner";
import { ToastProvider } from "./components/common/ToastProvider";
import { RequirePermission } from "./components/common/RequirePermission";
import { Permissions } from "./types/permissions";
import { usePermissions } from "./hooks/usePermissions";

function HomeRedirect() {
  const { hasPermission } = usePermissions();
  if (hasPermission(Permissions.PROJECT_VIEW)) {
    return <Navigate to="/projects" replace />;
  }
  if (hasPermission(Permissions.CANDIDATE_INGEST_MANUAL)) {
    return <Navigate to="/candidates/ingest" replace />;
  }
  return <Navigate to="/login" replace />;
}

const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const SignupConfirmPage = lazy(() => import("./pages/SignupConfirmPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const MeetingsPage = lazy(() => import("./pages/MeetingsPage"));
const MeetingDetailPage = lazy(() => import("./pages/MeetingDetailPage"));
const MeetingBookingPage = lazy(() => import("./pages/MeetingBookingPage"));
const MeetingRoomPage = lazy(() => import("./pages/MeetingRoomPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const CandidateProfilePage = lazy(() => import("./pages/CandidateProfilePage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const ClientDetailPage = lazy(() => import("./pages/ClientDetailPage"));
const DiscoverTalentPage = lazy(() => import("./pages/DiscoverTalentPage"));
const TalentPoolPage = lazy(() => import("./pages/TalentPoolPage"));
const TalentPoolDetailPage = lazy(() => import("./pages/TalentPoolDetailPage"));
const TalentPoolCandidateProfilePage = lazy(
  () => import("./pages/TalentPoolCandidateProfilePage")
);
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const CompanyDetailPage = lazy(() => import("./pages/CompanyDetailPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const SettingsProfilePage = lazy(() => import("./pages/SettingsProfilePage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const IngestCandidatePage = lazy(() => import("./pages/IngestCandidatePage"));
const IngestCandidateReviewPage = lazy(
  () => import("./pages/IngestCandidateReviewPage")
);
const CandidateCompareReviewPage = lazy(
  () => import("./pages/CandidateCompareReviewPage")
);
const MyIngestionsPage = lazy(() => import("./pages/MyIngestionsPage"));
const SearchTracesPage = lazy(() => import("./pages/SearchTracesPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const SearchTraceDetailPage = lazy(() => import("./pages/SearchTraceDetailPage"));

export default function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <Suspense
          fallback={
            <div
              className="flex min-h-screen items-center justify-center"
              role="status"
            >
              <GradientSpinner size={32} />
              <span className="sr-only">Loading page</span>
            </div>
          }
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/signup/confirm" element={<SignupConfirmPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/book" element={<MeetingBookingPage />} />
            {/* Public route — single meeting room (stage-based rendering) */}
            <Route
              path="/meetings/:meetingId/room"
              element={<MeetingRoomPage />}
            />
            <Route element={<AuthGuard />}>
              <Route element={<AppLayout />}>
                <Route index element={<HomeRedirect />} />
                <Route
                  path="chat"
                  element={
                    <RequirePermission
                      permission={Permissions.AI_CHAT}
                      fallback={<HomeRedirect />}
                    >
                      <ChatPage />
                    </RequirePermission>
                  }
                />
                <Route path="meetings" element={<MeetingsPage />} />
                <Route
                  path="meetings/:meetingId"
                  element={<MeetingDetailPage />}
                />
                <Route path="projects" element={<ProjectsPage />} />
                <Route
                  path="projects/:projectId"
                  element={<ProjectDetailPage />}
                />
                <Route
                  path="projects/:projectId/candidates/:candidateId"
                  element={<CandidateProfilePage />}
                />
                <Route path="clients" element={<ClientsPage />} />
                <Route
                  path="clients/:clientId"
                  element={<ClientDetailPage />}
                />
                <Route
                  path="talent"
                  element={
                    <RequirePermission
                      permission={Permissions.CANDIDATE_SEARCH}
                      fallback={<HomeRedirect />}
                    >
                      <DiscoverTalentPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="talent/candidates/:candidateId"
                  element={<TalentPoolCandidateProfilePage />}
                />
                <Route
                  path="candidates/ingest"
                  element={
                    <RequirePermission
                      permission={Permissions.CANDIDATE_INGEST_MANUAL}
                      fallback={<HomeRedirect />}
                    >
                      <IngestCandidatePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="candidates/ingest/:id/review"
                  element={
                    <RequirePermission
                      permission={Permissions.CANDIDATE_INGEST_MANUAL}
                      fallback={<HomeRedirect />}
                    >
                      <IngestCandidateReviewPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="candidates/compare/:existingId/:shadowId"
                  element={
                    <RequirePermission
                      permission={Permissions.CANDIDATE_INGEST_MANUAL}
                      fallback={<HomeRedirect />}
                    >
                      <CandidateCompareReviewPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="candidates/ingestions/mine"
                  element={
                    <RequirePermission
                      permission={Permissions.CANDIDATE_INGEST_MANUAL}
                      fallback={<HomeRedirect />}
                    >
                      <MyIngestionsPage />
                    </RequirePermission>
                  }
                />
                <Route path="talent-pools" element={<TalentPoolPage />} />
                <Route
                  path="talent-pools/:poolId"
                  element={<TalentPoolDetailPage />}
                />
                <Route
                  path="companies"
                  element={
                    <RequirePermission
                      permission={Permissions.COMPANY_VIEW}
                      fallback={<HomeRedirect />}
                    >
                      <CompaniesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="companies/:id"
                  element={
                    <RequirePermission
                      permission={Permissions.COMPANY_VIEW}
                      fallback={<HomeRedirect />}
                    >
                      <CompanyDetailPage />
                    </RequirePermission>
                  }
                />
                <Route path="feedback" element={<FeedbackPage />} />
                <Route path="feedback/:feedbackId" element={<FeedbackPage />} />
                <Route
                  path="settings/profile"
                  element={<SettingsProfilePage />}
                />
                <Route
                  path="admin/users"
                  element={
                    <RequirePermission
                      permission={Permissions.USER_VIEW}
                      fallback={<HomeRedirect />}
                    >
                      <UsersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="analytics"
                  element={
                    <RequirePermission
                      permission={Permissions.ANALYTICS_VIEW_OWN}
                      fallback={<HomeRedirect />}
                    >
                      <AnalyticsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="admin/search-traces"
                  element={
                    <RequirePermission
                      permission={Permissions.SEARCH_TRACE_VIEW}
                      fallback={<HomeRedirect />}
                    >
                      <SearchTracesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="admin/search-traces/:turn_id"
                  element={
                    <RequirePermission
                      permission={Permissions.SEARCH_TRACE_VIEW}
                      fallback={<HomeRedirect />}
                    >
                      <SearchTraceDetailPage />
                    </RequirePermission>
                  }
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ToastProvider>
  );
}
